/**
 * utils/thumbnails.js
 *
 * Exact URL patterns from server util.js:
 *
 * The API already returns built URLs in these fields:
 *   movie.thumbnail  → array of portrait URLs  (getThumbnailsPortrait/Landscape based on tnorientation)
 *   movie.image      → array of webp URLs       (getImage)
 *   movie.banner     → array of banner URLs     (getBanner/getBannerImageDevice)
 *   episode.episodethumbnail → array of episode portrait URLs
 *   episode.thumbnail        → array of film portrait URLs
 *
 * CDN URL patterns (fallback if API fields missing):
 *   Portrait:   {CDN}{filmid}/{filmid}-1.webp
 *   Portrait:   {CDN}{filmid}/{filmid}-P-1.png
 *   Landscape:  {CDN}{filmid}/{filmid}-L-1.png
 *   Banner:     {CDN}{filmid}/{filmid}-banner.webp
 *   Episode:    {CDN}{filmid}/{episodeid}/{filmid}-{episodeid}-1.png
 *   Ep Landscape: {CDN}{filmid}/{episodeid}/{filmid}-{episodeid}-L-c1.png
 */

// Branded domain — preferred for everything. Plain <img> tags don't trigger
// a CORS check the way fetch/XHR/HLS.js does, so the CORS restriction we
// found on this domain for trailers doesn't apply to regular image loading.
const CDN_BRANDED = 'https://cdn.shortflixindia.com/';
// Open CloudFront CDN — kept only as an automatic fallback if a specific
// asset genuinely isn't reachable on the branded domain for some reason.
const CDN_OPEN = 'https://d2q1oltu9dnqie.cloudfront.net/';
// Legacy alias kept for any code elsewhere in this file still referencing CDN directly.
const CDN = CDN_BRANDED;

// Returns [brandedUrl, openUrl] for a given filmid/suffix — SmartImage tries
// them in order via its existing onError fallback chain, so branded is
// always attempted first with the open CDN as a same-suffix safety net.
function cdnVariants(filmid, suffix, ext = 'webp') {
  if (!filmid) return [];
  return [
    `${CDN_BRANDED}${filmid}/${filmid}-${suffix}.${ext}`,
    `${CDN_OPEN}${filmid}/${filmid}-${suffix}.${ext}`,
  ];
}

function cdnUrl(filmid, suffix, ext = 'webp') {
  if (!filmid) return null;
  return `${CDN_BRANDED}${filmid}/${filmid}-${suffix}.${ext}`;
}

function getFilmId(movie) {
  return movie?.filmid || movie?.vid || movie?.id || null;
}

// Extract first valid URL from API field (array or string). The branded
// domain is left untouched (it's now the preferred domain) — only the dead
// legacy CloudFront ID gets normalized. Signed URLs (/t/{token}/) are never
// touched — those are real secure-video links and rewriting breaks them.
function normalizeCdnUrl(url) {
  if (!url) return url;
  if (url.includes('/t/')) return url; // signed — never rewrite
  return url.replace('https://d12it2ctp7su6p.cloudfront.net/', CDN_BRANDED);
}

function extractUrl(field) {
  if (!field) return null;
  if (typeof field === 'string' && field.startsWith('http')) return normalizeCdnUrl(field);
  if (Array.isArray(field) && field[0] && typeof field[0] === 'string') return normalizeCdnUrl(field[0]);
  return null;
}

// ── Portrait thumbnail (home page cards) ─────────────────────────────────────
// API returns: movie.image[0] = {filmid}-1.webp (getImage)
// API returns: movie.thumbnail[0] = portrait/landscape based on tnorientation
// We ALWAYS want portrait (-1.webp or -P-1.png) for home cards
export function buildPortraitSources(movie) {
  const id = getFilmId(movie);

  // Always try portrait-specific URLs first: -P-1.png > -1.webp
  // movie.image[0] = {filmid}-1.webp from getImage() — this is the app portrait thumb
  const imageUrl = extractUrl(movie?.image);
  const thumbUrl = extractUrl(movie?.thumbnail);
  // Only use thumbnail if it is portrait (not landscape -L-)
  const safeThumb = thumbUrl && !thumbUrl.includes('-L-') ? thumbUrl : null;

  return [
    ...cdnVariants(id, 'P-1', 'png'),     // {filmid}-P-1.png  ← portrait FIRST
    ...cdnVariants(id, 'P-1', 'webp'),    // {filmid}-P-1.webp
    safeThumb,                            // thumbnail if not landscape
    imageUrl,                             // image[0] = {filmid}-1.webp
    ...cdnVariants(id, '1', 'webp'),      // {filmid}-1.webp
    ...cdnVariants(id, '1', 'png'),
  ].filter(Boolean);
}

// ── Landscape thumbnail (wide cards / hover popup) ────────────────────────────
// Server sets movie.thumbnail based on tnorientation:
//   L orientation → {filmid}-L-1.png  (landscape — preferred)
//   V orientation → {filmid}-P-1.png  (portrait — fallback gracefully)
// For popup we try ALL landscape sources before falling back to portrait.
export function buildLandscapeSources(movie) {
  const id = getFilmId(movie);
  const thumbUrl  = extractUrl(movie?.thumbnail);
  const bannerUrl = extractUrl(movie?.banner);
  const imageUrl  = extractUrl(movie?.image);

  // Prefer landscape thumbnail, then CDN L-1 patterns, then banner, then portrait
  const landscapeThumb = thumbUrl && thumbUrl.includes('-L-') ? thumbUrl : null;

  return [
    landscapeThumb,                       // thumbnail if landscape (-L-)
    ...cdnVariants(id, 'L-1', 'png'),     // {filmid}-L-1.png  ← try branded, then open
    ...cdnVariants(id, 'L-1', 'webp'),    // {filmid}-L-1.webp
    // Only fall back to banner/portrait if no landscape found
    bannerUrl,                            // banner last (too wide/promotional)
    thumbUrl,
    imageUrl,
    ...cdnVariants(id, 'P-1', 'png'),
    ...cdnVariants(id, '1', 'webp'),
  ].filter(Boolean);
}

// ── Hero banner (hero section + detail page background) ───────────────────────
// API returns: movie.banner[0] = {filmid}-{device}-banner.png (getBannerImageDevice)
export function buildHeroSources(movie) {
  const id = getFilmId(movie);

  return [
    extractUrl(movie?.banner),                       // banner[0] ✅
    extractUrl(movie?.bannerImage),                  // bannerImage[0]
    ...cdnVariants(id, 'banner', 'webp'),            // {filmid}-banner.webp
    ...cdnVariants(id, 'banner', 'png'),             // {filmid}-banner.png
    ...cdnVariants(id, 'mobile-banner', 'png'),      // {filmid}-mobile-banner.png
    ...cdnVariants(id, 'L-1', 'png'),                // landscape fallback
    ...cdnVariants(id, '1', 'webp'),                 // portrait last resort
  ].filter(Boolean);
}

// ── Detail page poster (show detail left column) ──────────────────────────────
// Use portrait: {filmid}-P-1.png or {filmid}-1.webp
export function buildPosterSources(movie) {
  const id = getFilmId(movie);
  const imageUrl = extractUrl(movie?.image);

  return [
    imageUrl,                              // image[0] = {filmid}-1.webp
    ...cdnVariants(id, 'P-1', 'png'),      // {filmid}-P-1.png ← portrait poster
    ...cdnVariants(id, 'P-1', 'webp'),
    ...cdnVariants(id, '1', 'webp'),       // {filmid}-1.webp
    ...cdnVariants(id, '1', 'png'),
  ].filter(Boolean);
}

// ── Episode thumbnail ─────────────────────────────────────────────────────────
// API: episode.episodethumbnail[0] = {CDN}{filmid}/{episodeid}/{filmid}-{episodeid}-1.png
// URL from user: cdn.shortflixindia.com/romeo_juliet/short1/romeo_juliet-short1-1.png
export function buildEpisodeSources(episode, parentFilmId) {
  const fid = parentFilmId || getFilmId(episode);
  const eid = episode?.episodeid;

  return [
    // API episodethumbnail field is most reliable
    extractUrl(episode?.episodethumbnail),
    extractUrl(episode?.episodeImage),
    // CDN pattern: {filmid}/{episodeid}/{filmid}-{episodeid}-1.png
    fid && eid && `${CDN_BRANDED}${fid}/${eid}/${fid}-${eid}-1.png`,
    fid && eid && `${CDN_OPEN}${fid}/${eid}/${fid}-${eid}-1.png`,
    fid && eid && `${CDN_BRANDED}${fid}/${eid}/${fid}-${eid}-1.webp`,
    // Landscape episode: {filmid}-{episodeid}-L-c1.png
    fid && eid && `${CDN_BRANDED}${fid}/${eid}/${fid}-${eid}-L-c1.png`,
    // Film fallback thumbnail
    extractUrl(episode?.thumbnail),
    ...cdnVariants(fid, '1', 'webp'),
  ].filter(Boolean);
}

// ── Main export ───────────────────────────────────────────────────────────────
export function buildImageSources(movie, type = 'portrait') {
  switch (type) {
    case 'portrait':  return buildPortraitSources(movie);
    case 'landscape': return buildLandscapeSources(movie);
    case 'hero':      return buildHeroSources(movie);
    case 'poster':    return buildPosterSources(movie);
    default:          return buildPortraitSources(movie);
  }
}

export const getHeroBanner  = (m) => buildHeroSources(m)[0] || null;
export const getDetailPoster = (m) => buildPosterSources(m)[0] || null;
